export type ParsedValue =
  | { kind: "flat"; amount: number; from: boolean }
  | { kind: "percent"; percent: number }
  | { kind: "ask" }
  | { kind: "dont" };
export type BackgroundType = "none" | "simple" | "complex";
export type UsageType = "personal" | "commercial";
export type ContentRating = "sfw" | "suggestive" | "nsfw";
export type FeeKey = string;

export const CANONICAL_FEES = [
  "background_simple", "background_complex", "extra_character",
  "complex_design", "nsfw_content", "commercial_use", "rush_order",
] as const;

/** Fees applied to the running subtotal (everything except bg + extra chars). */
const DERIVED_ONLY = new Set(["background_simple", "background_complex", "extra_character"]);

export const DEFAULT_FEE_LABELS: Record<string, string> = {
  background_simple: "Simple background",
  background_complex: "Complex background",
  extra_character: "Extra characters",
  complex_design: "Complex design",
  nsfw_content: "NSFW / suggestive",
  commercial_use: "Commercial use",
  rush_order: "Rush order",
};

function normalizeText(raw: string): string {
  return raw.trim().toLowerCase().replace(/[’`]/g, "'");
}

function toNumber(token: string): number {
  // "9,5" → 9.5 ; "1,000" → 1000
  const t = /^\d+,\d{1,2}$/.test(token) ? token.replace(",", ".") : token.replace(/,/g, "");
  return Number(t);
}

export function parseValue(raw: string | number | null | undefined): ParsedValue {
  if (raw === null || raw === undefined) return { kind: "dont" };
  if (typeof raw === "number") return Number.isFinite(raw) ? { kind: "flat", amount: raw, from: false } : { kind: "ask" };
  const s = normalizeText(raw);
  if (s === "") return { kind: "dont" };
  if (s === "don't do" || s === "dont do" || s === "no") return { kind: "dont" };
  if (s === "ask") return { kind: "ask" };

  const pct = s.match(/(-?\d+(?:[.,]\d+)?)\s*%/);
  if (pct) return { kind: "percent", percent: toNumber(pct[1]) };

  const nums = s.match(/\d+(?:[.,]\d+)*/g);
  if (!nums) return { kind: "ask" };
  const amount = toNumber(nums[0]);
  if (!Number.isFinite(amount)) return { kind: "ask" };
  const from = nums.length > 1 || /\bfrom\b|a partir/.test(s) || /\d\s*[-–~]\s*\$?\d/.test(s);
  return { kind: "flat", amount, from };
}

export interface CommissionTypeOption { key: string; label: string; price: ParsedValue }

function isAddOnValue(value: string | number): boolean {
  return typeof value === "string" && value.trim().startsWith("+");
}

export function listCommissionTypes(prices: Record<string, string | number> | null): CommissionTypeOption[] {
  if (!prices) return [];
  return Object.entries(prices)
    .filter(([, v]) => !isAddOnValue(v))
    .map(([key, v]) => ({ key, label: key.trim(), price: parseValue(v) }));
}

/** Maps a human or snake_case fee name to canonical key(s). */
export function canonicalFeeKeys(name: string): string[] {
  const n = normalizeText(name).replace(/[_-]+/g, " ").replace(/\s+/g, " ");
  if (/^(simple )?(bg|background) simple$|^simple (bg|background)$/.test(n)) return ["background_simple"];
  if (/^(bg|background) complex$|^complex (bg|background)$/.test(n)) return ["background_complex"];
  if (/^(bg|background)$/.test(n)) return ["background_simple", "background_complex"];
  if (/^extra (character|char)s?$/.test(n)) return ["extra_character"];
  if (/^complex design$/.test(n)) return ["complex_design"];
  if (/^nsfw( content)?$/.test(n)) return ["nsfw_content"];
  if (/^commercial( use)?$/.test(n)) return ["commercial_use"];
  if (/^rush( order)?$/.test(n)) return ["rush_order"];
  return [n.replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "")];
}

export function normalizeFees(
  prices: Record<string, string | number> | null,
  additionalFees: Record<string, string> | null,
): Record<FeeKey, string> {
  const out: Record<FeeKey, string> = {};
  for (const [name, value] of Object.entries(prices ?? {})) {
    if (!isAddOnValue(value)) continue;
    for (const key of canonicalFeeKeys(name)) out[key] = String(value);
  }
  for (const [name, value] of Object.entries(additionalFees ?? {})) {
    if (typeof value !== "string") continue;
    for (const key of canonicalFeeKeys(name)) out[key] = value;
  }
  return out;
}

/** A deadline closer than this many days gets the artist's rush fee automatically. */
export const RUSH_DAYS = 5;

/** Fees the client never picks: the artist applies complex design when quoting, rush comes from the deadline. */
export const NOT_CLIENT_PICKED = new Set(["complex_design", "rush_order"]);

/** UTC calendar date (YYYY-MM-DD). Client and server both use UTC so they agree on the rush rule. */
export function todayUTC(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

export function isRushDeadline(deadline: string | null | undefined, today: string): boolean {
  if (!deadline) return false;
  const d = Date.parse(`${deadline}T00:00:00Z`), t = Date.parse(`${today}T00:00:00Z`);
  if (!Number.isFinite(d) || !Number.isFinite(t)) return false;
  return (d - t) / 86_400_000 < RUSH_DAYS;
}

export function deriveSelectedFees(args: {
  fees: Record<FeeKey, string>; manual: FeeKey[]; usage: UsageType; rating: ContentRating;
  deadline?: string | null; today?: string;
}): FeeKey[] {
  const { fees, manual, usage, rating } = args;
  const offered = (k: string) => k in fees && parseValue(fees[k]).kind !== "dont";
  const picked: FeeKey[] = [];
  const add = (k: string) => { if (!DERIVED_ONLY.has(k) && offered(k) && !picked.includes(k)) picked.push(k); };
  manual.filter((k) => !NOT_CLIENT_PICKED.has(k)).forEach(add);
  if (usage !== "personal") add("commercial_use");
  if (rating !== "sfw") add("nsfw_content");
  if (isRushDeadline(args.deadline, args.today ?? todayUTC())) add("rush_order");
  return picked;
}

export interface EstimateLine { key: string; label: string; amount: number | null; kind: "base" | "fee" | "ask"; from: boolean }
export interface Estimate { lines: EstimateLine[]; total: number; hasAsk: boolean; hasFrom: boolean }
export interface EstimateInput {
  prices: Record<string, string | number> | null;
  additionalFees: Record<string, string> | null;
  /** One entry per character: the body type (artist price key) each character is drawn as. */
  characterTypes: string[];
  backgroundType: BackgroundType;
  selectedFees: FeeKey[];
  /** Label overrides by fee key; extra_character may contain "{type}". */
  labels?: Partial<Record<string, string>>;
}

const flatPrice = (prices: Record<string, string | number>, type: string): number | null => {
  const v = parseValue(prices[type]);
  return v.kind === "flat" ? v.amount : null;
};

/** The character type that pays full price: the most expensive offered type (first on ties). */
export function primaryType(prices: Record<string, string | number> | null, types: string[]): string | null {
  const offered = types.filter((t) => !!prices && t in prices);
  if (offered.length === 0) return null;
  let best = offered[0];
  for (const t of offered) {
    if ((flatPrice(prices!, t) ?? -1) > (flatPrice(prices!, best) ?? -1)) best = t;
  }
  return best;
}

/** "Bust × 3" when every character shares a type, otherwise "Full Body + Bust"; then " · Name". */
export function briefSubject(types: string[], firstName?: string): string {
  const clean = types.map((t) => t.trim());
  const body = clean.length > 0 && clean.every((t) => t === clean[0]) ? `${clean[0]} × ${clean.length}` : clean.join(" + ");
  return firstName ? `${body} · ${firstName}` : body;
}

export function computeEstimate(input: EstimateInput): Estimate {
  const { prices, additionalFees, characterTypes, backgroundType, selectedFees, labels = {} } = input;
  const empty: Estimate = { lines: [], total: 0, hasAsk: false, hasFrom: false };
  const types = characterTypes.filter((t) => !!prices && t in prices).slice(0, 5);
  const primary = primaryType(prices, types);
  if (!prices || !primary) return empty;

  const fees = normalizeFees(prices, additionalFees);
  const label = (key: string) =>
    labels[key] ?? DEFAULT_FEE_LABELS[key] ?? key.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
  const lines: EstimateLine[] = [];
  const askLine = (key: string, text: string) => lines.push({ key, label: text, amount: null, kind: "ask", from: false });

  const base = parseValue(prices[primary]);
  const baseAmount = base.kind === "flat" ? base.amount : 0;
  if (base.kind === "flat") {
    lines.push({ key: "base", label: primary.trim(), amount: base.amount, kind: "base", from: base.from });
  } else {
    askLine("base", primary.trim());
  }

  // An "Ask" base has no number to take a percentage of, so percentage fees become ask lines too.
  const baseIsPriced = base.kind === "flat";
  let subtotal = baseAmount;
  const addFee = (key: string, text: string, v: ParsedValue, percentBasis: number) => {
    if (v.kind === "flat") {
      lines.push({ key, label: text, amount: v.amount, kind: "fee", from: v.from });
      return v.amount;
    }
    if (v.kind === "percent" && baseIsPriced) {
      const amount = Math.round((percentBasis * v.percent) / 100);
      lines.push({ key, label: text, amount, kind: "fee", from: false });
      return amount;
    }
    askLine(key, text);
    return 0;
  };

  if (backgroundType !== "none") {
    const key = backgroundType === "simple" ? "background_simple" : "background_complex";
    const v = key in fees ? parseValue(fees[key]) : ({ kind: "ask" } as const);
    if (v.kind !== "dont") subtotal += addFee(key, label(key), v, baseAmount);
  }

  // Every character except the primary gets the artist's extra-character promotion on ITS OWN type's price.
  const extraFee = "extra_character" in fees ? parseValue(fees.extra_character) : ({ kind: "ask" } as const);
  const primaryIndex = types.indexOf(primary);
  const extraTemplate = labels.extra_character ?? "Extra character · {type}";
  types.forEach((type, i) => {
    if (i === primaryIndex) return;
    const text = extraTemplate.replace("{type}", type.trim());
    const own = flatPrice(prices, type);
    if (extraFee.kind === "flat") { subtotal += addFee("extra_character", text, extraFee, 0); return; }
    if (extraFee.kind === "percent" && own !== null && baseIsPriced) { subtotal += addFee("extra_character", text, extraFee, own); return; }
    askLine("extra_character", text);
  });

  const fullSum = types.reduce((n, t) => n + (flatPrice(prices, t) ?? 0), 0);
  let percentTotal = 0;
  for (const key of selectedFees) {
    if (DERIVED_ONLY.has(key) || !(key in fees)) continue;
    const v = parseValue(fees[key]);
    if (v.kind === "dont") continue;
    percentTotal += addFee(key, label(key), v, key === "complex_design" ? fullSum : subtotal);
  }

  const total = subtotal + percentTotal;
  return { lines, total, hasAsk: lines.some((l) => l.kind === "ask"), hasFrom: lines.some((l) => l.from) };
}

export function formatMoney(amount: number): string {
  const fractional = Math.round(amount * 100) % 100 !== 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD",
    minimumFractionDigits: fractional ? 2 : 0, maximumFractionDigits: fractional ? 2 : 0,
  }).format(amount);
}

/** True when the base price itself is "Ask": the artist quotes everything, so no total is shown. */
export function isQuoteOnly(e: Estimate): boolean {
  return e.lines[0]?.key === "base" && e.lines[0].kind === "ask";
}
